import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import type { Site } from '../types';

interface UseSitesReturn {
  sites: Site[];
  addSite: (name: string, projectId: string | null, color: string) => Promise<Site | null>;
}

export function useSites(projectId: string | null, orgId: string | null = null): UseSitesReturn {
  const [sites, setSites] = useState<Site[]>([]);

  useEffect(() => {
    if (!projectId) {
      setSites([]);
      return;
    }

    let query = supabase.from('sites').select('*').order('name');

    if (orgId) {
      // Fetch project-specific sites + org-wide sites (project_id IS NULL)
      query = query
        .or(`project_id.eq.${projectId},project_id.is.null`)
        .eq('organization_id', orgId);
    } else {
      query = query.eq('project_id', projectId);
    }

    query
      .then(({ data }) => setSites(data ?? []))
      .catch(() => setSites([]));
  }, [projectId, orgId]);

  const addSite = async (name: string, siteProjectId: string | null, color: string): Promise<Site | null> => {
    if (!orgId) return null;
    try {
      const { data, error } = await supabase
        .from('sites')
        .insert({ name, project_id: siteProjectId, color, organization_id: orgId, archived: false })
        .select()
        .single();
      if (error) throw error;
      const created = data as Site;
      setSites((prev) => sortByName([...prev, created]));
      return created;
    } catch {
      return null;
    }
  };

  return { sites, addSite };
}
